import { Box } from '@mantine/core';
import { useEffect } from 'react';
import UnderlineTabsDeprecated from 'src/UI/page-nav/UnderlineTabsDeprecated';
import { UserDetailsTabs, useUserDetailsTabs } from './hooks/useUserDetailsTabs';
import UserDetails from './components/UserDetails';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { useNavigate, useParams } from 'react-router';
import PasswordManagement from './components/PasswordManagement';
import { isSsoOnly } from 'src/auth/utils/ory-helpers';
import { AuthMethod } from 'src/auth/settings/types';

export default function UserDetailsPage() {
  const { currentTab, setTab } = useUserDetailsTabs();
  const {
    currentUser,
    isLoading: currentUserLoading,
    refetch: refetchCurrentUser,
  } = useCurrentUser();
  const { tab, id } = useParams<{ tab: UserDetailsTabs; id?: string }>();
  const navigate = useNavigate();

  const ssoOnly = isSsoOnly(currentUser?.tenantAuthMethod as AuthMethod | undefined);

  useEffect(() => {
    if (currentUserLoading) {
      return;
    }

    if (!currentUser) {
      navigate('/');
      return;
    }

    if (!id) {
      navigate(`/user/${UserDetailsTabs.MyDetails}/${currentUser.id}`, { replace: true });
      return;
    }

    if (!tab || !Object.values(UserDetailsTabs).includes(tab as UserDetailsTabs)) {
      navigate(`/user/${UserDetailsTabs.MyDetails}/${currentUser.id}`, { replace: true });
      return;
    }

    if (id && id !== currentUser.id) {
      navigate(`/user/${currentTab}/${currentUser.id}`, { replace: true });
      return;
    }

    if (ssoOnly && tab === UserDetailsTabs.Password) {
      navigate(`/user/${UserDetailsTabs.MyDetails}/${currentUser.id}`, { replace: true });
      return;
    }
  }, [currentUserLoading, currentUser, id, tab, currentTab, navigate, ssoOnly]);

  useEffect(() => {
    if (tab && currentTab !== tab) {
      setTab(tab as UserDetailsTabs);
    }
  }, [tab, currentTab, setTab]);

  if (!currentUser || !tab || (id && id !== currentUser.id)) {
    return null;
  }

  const getTabContent = () => {
    switch (currentTab) {
      case UserDetailsTabs.MyDetails:
        return <UserDetails user={currentUser} onUpdateSuccess={handleUpdateSuccess} />;
      case UserDetailsTabs.Password:
        return <PasswordManagement />;
      default:
        return <UserDetails user={currentUser} onUpdateSuccess={handleUpdateSuccess} />;
    }
  };

  const handleUpdateSuccess = async () => {
    await refetchCurrentUser();
  };

  return (
    <Box p="md">
      <UnderlineTabsDeprecated
        value={currentTab}
        onChange={value => {
          if (value != null) {
            setTab(value as UserDetailsTabs);
            navigate(`/user/${value}/${currentUser.id}`, { replace: true });
          }
        }}
      >
        <UnderlineTabsDeprecated.List>
          <UnderlineTabsDeprecated.Tab value={UserDetailsTabs.MyDetails}>
            My Details
          </UnderlineTabsDeprecated.Tab>
          {!ssoOnly && (
            <UnderlineTabsDeprecated.Tab value={UserDetailsTabs.Password}>
              Password
            </UnderlineTabsDeprecated.Tab>
          )}
        </UnderlineTabsDeprecated.List>

        <Box mt="sm">{getTabContent()}</Box>
      </UnderlineTabsDeprecated>
    </Box>
  );
}
